"use client";

import { useEffect, useState } from "react";
import { getInAppBrowserType } from "@/lib/utils/in-app-browser";
import type { OAuthProvider } from "@/lib/auth/in-app-precheck";

interface InAppOAuthBlock {
  /** 인앱 브라우저인지 여부 (마운트 후에만 true) */
  inApp: boolean;
  /** provider별로 인앱에서 차단/우회 대상인지 — UI에서 자물쇠 아이콘 등 시각화에 사용 */
  isProviderBlocked: (provider: OAuthProvider) => boolean;
}

/**
 * 인앱 브라우저 환경에서 어떤 OAuth provider가 차단/우회 대상인지 알려주는 훅.
 *
 * 차단 대상:
 * - Google/Apple: 모든 인앱에서 disallowed_useragent
 * - 같은 앱 내부 OAuth: KakaoTalk×Kakao, LINE×LINE, Naver×Naver (무한 루프 위험)
 * - iOS Meta(Instagram/Facebook/Threads/Twitter)에서는 모든 소셜 OAuth가 사용자 액션 필요
 *
 * SSR 안전: 초기 inApp=false, 마운트 후 1회 갱신.
 */
export function useInAppOAuthBlock(): InAppOAuthBlock {
  const [type, setType] = useState<ReturnType<typeof getInAppBrowserType>>(
    "unknown",
  );

  useEffect(() => {
    const detected = getInAppBrowserType();
    if (detected === "unknown") return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setType(detected);
  }, []);

  const inApp = type !== "unknown";

  const isProviderBlocked = (provider: OAuthProvider): boolean => {
    if (!inApp) return false;
    if (provider === "google" || provider === "apple") return true;
    if (provider === "kakao" && type === "kakaotalk") return true;
    if (provider === "line" && type === "line") return true;
    if (provider === "naver" && type === "naver") return true;
    // iOS Meta 계열에서는 시각적 경고를 위해 모든 provider 표시
    if (
      type === "instagram" ||
      type === "facebook" ||
      type === "threads" ||
      type === "twitter"
    ) {
      return true;
    }
    return false;
  };

  return { inApp, isProviderBlocked };
}
